import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ChevronRight, MapPinned, X, Save, Maximize2, Pentagon, Loader2, Pencil, Move, Copy, Plus, Trash2 } from 'lucide-react';
import { apiCall } from '@/api/apiCall';
import { GOOGLE_MAP_KEY } from '@/constants/constant';
import { getDeviceColor, getMarkerIconPaths, getDeviceTypeName } from '@/components/child/offStreetDeviceVisuals';
import ReactSelect from '@/components/common/ReactSelect';
import OffStreetPolygonEditShimmer from './OffStreetPolygonEditShimmer';

// ─── Labels toggle ke liye map styles (OffStreetMapPage jaisa) ────────────────
const getDarkMapStyles = (hideLabels = false) => [
  { elementType: 'geometry', stylers: [{ color: '#1a1d28' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1a1d28' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8b92a8' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#6b7280' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2c3142' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f1419' }] },
  ...(hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : []),
];

const getLightMapStyles = (hideLabels = false) => [
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#ffffff' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
  ...(hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : []),
];

// 👇 Hybrid (Satellite View) mein sirf labels hide karne ke liye — baaki satellite
// imagery waisi hi rehti hai. Google Maps 'hybrid' mapType par bhi styles apply hote hain.
const getHybridMapStyles = (hideLabels = false) =>
  hideLabels ? [{ elementType: 'labels', stylers: [{ visibility: 'off' }] }] : [];

const LEVEL_COLORS = { COUNTRY: '#6366F1', CITY: '#3B82F6', OPS: '#06B6D4', LOCATION: '#10B981', GATE: '#F59E0B' };
const POLYGON_CORNER_COUNT = 4;
const CREATE_DEVICE_ID = '__new_device__';

function normalizePolygonCoords(coords) {
  if (!Array.isArray(coords) || !coords.length) return [];
  let result = coords;
  while (Array.isArray(result[0])) result = result.flat();
  return result;
}

function clearShadow(shadow) {
  if (!shadow) return;
  if (Array.isArray(shadow)) shadow.forEach((s) => s?.setMap(null));
  else shadow.setMap(null);
}
function setShadowPath(shadow, path) {
  if (!shadow) return;
  if (Array.isArray(shadow)) shadow.forEach((s) => s.setPath(path));
  else shadow.setPath(path);
}

function createThickPolygon(google, paths, color, map, options = {}) {
  const shadowLayers = [
    { weight: 24, opacity: 0.04 },
    { weight: 19, opacity: 0.06 },
    { weight: 14, opacity: 0.09 },
    { weight: 10, opacity: 0.13 },
    { weight: 6, opacity: 0.18 },
  ].map(({ weight, opacity }) =>
    new google.maps.Polygon({
      paths,
      strokeColor: color,
      strokeWeight: weight,
      strokeOpacity: opacity,
      fillColor: color,
      fillOpacity: 0,
      clickable: false,
      zIndex: 0,
      map,
    })
  );

  const polygon = new google.maps.Polygon({
    paths,
    strokeColor: color,
    strokeWeight: 2.5,
    strokeOpacity: 1,
    fillColor: color,
    fillOpacity: 0.16,
    clickable: true,
    editable: false,
    zIndex: 1,
    map,
    ...options,
  });

  const path = polygon.getPath();
  ['insert_at', 'remove_at', 'set_at'].forEach((evt) => {
    google.maps.event.addListener(path, evt, () => {
      const arr = path.getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
      setShadowPath(shadowLayers, arr);
    });
  });

  return {
    polygon,
    shadow: shadowLayers,
    setMap: (m) => { polygon.setMap(m); shadowLayers.forEach((s) => s.setMap(m)); },
  };
}

const collectDescendantPoints = (row, rowsIndex) => {
  const points = [];
  const stack = [row];
  while (stack.length) {
    const cur = stack.pop();
    if (cur.level === 'DEVICE' && cur.lat != null && cur.lng != null) points.push({ lat: cur.lat, lng: cur.lng });
    (rowsIndex.get(cur.id) || []).forEach((c) => stack.push(c));
  }
  return points;
};
const collectDirectChildDevices = (row, rowsIndex) => (rowsIndex.get(row.id) || []).filter((r) => r.level === 'DEVICE');

const groupDevicesByCoord = (devices, precision = 6) => {
  const map = new Map();
  devices.forEach((d) => {
    const key = `${d.lat.toFixed(precision)},${d.lng.toFixed(precision)}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(d);
  });
  return Array.from(map.values());
};
const polygonCentroid = (coords) => ({
  lat: coords.reduce((s, p) => s + p.lat, 0) / coords.length,
  lng: coords.reduce((s, p) => s + p.lng, 0) / coords.length,
});

function createLabelDeclutterer(minSepDeg = 0.0018, maxAttempts = 24) {
  let placed = [];
  return {
    reset() { placed = []; },
    place(lat, lng) {
      let candidate = { lat, lng };
      let attempt = 0;
      while (
        attempt < maxAttempts &&
        placed.some((p) => Math.hypot(p.lat - candidate.lat, p.lng - candidate.lng) < minSepDeg)
      ) {
        attempt += 1;
        const angle = attempt * 137.5 * (Math.PI / 180);
        const radius = minSepDeg * 0.9 * attempt;
        candidate = { lat: lat + Math.sin(angle) * radius, lng: lng + Math.cos(angle) * radius };
      }
      placed.push(candidate);
      return candidate;
    },
  };
}

function loadScriptTag(id, src) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === 'true') { resolve(); return; }
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Maps script')));
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true; script.defer = true;
    script.onload = () => { script.dataset.loaded = 'true'; resolve(); };
    script.onerror = () => reject(new Error('Failed to load Google Maps script'));
    document.head.appendChild(script);
  });
}

async function ensureGoogleMapsLoaded() {
  if (!window.google?.maps) {
    await loadScriptTag('google-maps-script-core', `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=geometry&v=weekly`);
  }
  if (window.google?.maps && !window.google.maps.geometry && typeof window.google.maps.importLibrary === 'function') {
    try { await window.google.maps.importLibrary('geometry'); } catch (e) { /* non-critical */ }
  }
}

function buildMarkerIcon(google, deviceType, color, selected = false, armed = false) {
  const size = armed ? 52 : (selected ? 46 : 34);
  const ringColor = '#22c55e';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
    ${armed ? `<circle cx="32" cy="28" r="29" fill="none" stroke="${ringColor}" stroke-width="3" stroke-dasharray="6 5"><animateTransform attributeName="transform" type="rotate" from="0 32 28" to="360 32 28" dur="2.2s" repeatCount="indefinite"/></circle>` : ''}
    <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="${selected || armed ? 1 : 0.9}"/>
    <circle cx="32" cy="25" r="11" fill="white"/>
    <g transform="translate(25,18) scale(0.583)" fill="none" stroke="${color}" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
      ${getMarkerIconPaths(deviceType)}
    </g>
    ${armed ? `<circle cx="32" cy="25" r="12.5" fill="none" stroke="${ringColor}" stroke-width="2.5"/>` : ''}
  </svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(size, size), anchor: new google.maps.Point(size / 2, size) };
}
function buildClusterMarkerIcon(google, count, color) {
  const size = 34;
  const label = count > 99 ? '99+' : String(count);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 64 64">
    <path d="M32 3C20 3 10 13 10 25c0 18 22 36 22 36s22-18 22-36C54 13 44 3 32 3z" fill="${color}" opacity="0.9"/>
    <circle cx="32" cy="25" r="13" fill="white"/>
    <text x="32" y="26" font-family="sans-serif" font-size="${label.length > 2 ? 11 : 15}" font-weight="800" fill="${color}" text-anchor="middle" dominant-baseline="central">${label}</text>
  </svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(size, size), anchor: new google.maps.Point(size / 2, size) };
}
function buildEntityLabelIcon(google, text, color) {
  const w = Math.max(70, text.length * 7 + 24);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="26" viewBox="0 0 ${w} 26">
    <rect x="0" y="0" width="${w}" height="26" rx="7" fill="${color}" opacity="0.94"/>
    <text x="${w / 2}" y="13" font-family="sans-serif" font-size="9" font-weight="700" fill="white" text-anchor="middle" dominant-baseline="central">${text}</text>
  </svg>`;
  return { url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`, scaledSize: new google.maps.Size(w, 26), anchor: new google.maps.Point(w / 2, 13) };
}

export function buildPolygonPayload(row, coordinates) {
  const { country, city, op, loc, gate } = row.path || {};
  const payload = { level_type: row.level };
  if (country) payload.country = country.country;
  if (city) payload.city_cd = city.city_cd;
  if (op) payload.ops = op.ops;
  if (loc) payload.location = loc.location;
  if (gate) payload.gate = gate.gate_id ?? gate.gate;
  payload.coordinates = coordinates;
  return payload;
}

export async function savePolygonToApi(row, coordinates) {
  const payload = buildPolygonPayload(row, coordinates);
  const res = await apiCall('post', '/off-street-polygon/upsert', payload, null, false, true);
  if (res?.success) return res.fields?.data ?? payload;
  throw new Error(res?.message || 'Failed to save polygon');
}

export function buildDuplicateDevicePayload(device) {
  const raw = device.raw || {};
  const lat = device.lat ?? raw.latitude;
  const lng = device.lng ?? raw.longitude;
  return {
    row_id: raw.row_id ?? device.rowId ?? device.id,
    device_id: raw.device_id ?? device.deviceId ?? device.code,
    device_name: device.name ?? raw.device_name,
    device_type: device.deviceType ?? raw.device_type,
    latitude: lat != null ? Number(lat).toFixed(6) : lat,
    longitude: lng != null ? Number(lng).toFixed(6) : lng,
  };
}

export async function duplicateDeviceApi(payload) {
  const res = await apiCall('post', '/off-street-device/duplicate', payload, null, false, true);
  if (res?.success) return res.fields?.data ?? payload;
  throw new Error(res?.message || 'Failed to duplicate device');
}

export async function createDeviceApi(payload) {
  const res = await apiCall('post', '/off-street-device/duplicate', payload, null, false, true);
  if (res?.success) return res.fields?.data ?? payload;
  throw new Error(res?.message || 'Failed to create device');
}

export async function deleteDeviceApi(rowId) {
  const res = await apiCall('post', '/off-street-device/delete', { row_id: rowId }, null, false, true);
  if (res?.success) return true;
  throw new Error(res?.message || 'Failed to delete device');
}

function buildHierarchyQuery(row) {
  const { country, city, op, loc, gate } = row.path || {};
  const params = new URLSearchParams();
  if (country?.country) params.set('country', country.country);
  if (city?.city_cd) params.set('city_cd', city.city_cd);
  if (op?.ops) params.set('ops', op.ops);
  if (loc?.location) params.set('location', loc.location);
  if (gate) params.set('gate', gate.gate_id ?? gate.gate);
  return params.toString();
}

function buildHierarchyFields(row) {
  const { country, city, op, loc, gate } = row.path || {};
  return {
    country: country?.country ?? '',
    city_cd: city?.city_cd ?? '',
    ops: op?.ops ?? '',
    location: loc?.location ?? '',
    gate: gate ? (gate.gate_id ?? gate.gate ?? '') : '',
    gate_name: gate?.gate_name ?? '',
  };
}

async function fetchPolygonHierarchy(row) {
  const qs = buildHierarchyQuery(row);
  if (!qs) return [];
  const res = await apiCall('get', `/off-street-polygon/get-all-under?${qs}`, null, null, false, false);
  if (res?.success) return res.data || [];
  throw new Error(res?.message || 'Failed to fetch polygon hierarchy');
}

function apiNodeToRow(node) {
  const path = {};
  if (node.country) path.country = { country: node.country };
  if (node.city_cd) path.city = { city_cd: node.city_cd };
  if (node.ops) path.op = { ops: node.ops };
  if (node.location) path.loc = { location: node.location };
  if (node.gate) path.gate = { gate_id: node.gate, gate_name: node.gate_name ?? '' };
  const code = node.gate || node.location || node.ops || node.city_cd || node.country || `#${node.id}`;
  return {
    id: `api-${node.level_type}-${node.id ?? code}`,
    apiId: node.id,
    isApiNode: true,
    name: code,
    code,
    level: node.level_type,
    path,
    raw: { polygon: normalizePolygonCoords(node.coordinates) },
    childCount: node.children?.length ?? 0,
  };
}

export default function PolygonMapEditor({
  row, rowsIndex: rowsIndexProp, polygon,
  onDeviceSave,
  onDevicesCoordsSave,
  onDeviceDuplicated,
  onDeviceCreated,
  onDeviceDeleted,
  onPolygonSaved,
  onChildPolygonSaved,
  onClose, onJumpTo,
  breadcrumb = [], onBreadcrumbClick, isDarkMode = false,
}) {
  const mapDivRef = useRef(null);
  const mapRef = useRef(null);
  const activePolygonRef = useRef(null);
  const activeShadowRef = useRef(null);
  const childMarkersRef = useRef([]);
  const deviceMarkerRef = useRef(null);
  const labelDeclutterRef = useRef(createLabelDeclutterer());
  const requestCloseRef = useRef(() => {});
  const mapTypeToggleRef = useRef(null);

  // 👇 Labels toggle ke liye refs
  const labelsVisibleRef = useRef(true);
  const labelsHideTimerRef = useRef(null);

  // 👇 mapType / isDarkMode ko ref mein bhi rakhna — stable callbacks ke liye
  const mapTypeRef = useRef('roadmap');
  const isDarkModeRef = useRef(isDarkMode);

  const childPolygonsRef = useRef(new Map());
  const activeChildPolygonRef = useRef(null);
  const childOriginalPathRef = useRef(null);
  const mainOriginalPathRef = useRef(null);

  const apiPolygonsMapRef = useRef(new Map());
  const hierarchyFetchGenRef = useRef(0);

  const isDrawingRef = useRef(false);
  const drawPointsRef = useRef([]);
  const tempPolygonRef = useRef(null);
  const tempShadowRef = useRef(null);
  const tempMarkersRef = useRef([]);
  const drawTargetRef = useRef(row);

  const deviceOverridesRef = useRef(new Map());
  const pulledOutIdsRef = useRef(new Set());
  const renderChildrenRef = useRef(() => {});

  // ── NEW: Locally-deleted device IDs — turant marker gayab karne ke liye.
  const [locallyDeletedIds, setLocallyDeletedIds] = useState(new Set());

  // rowsIndex wrapper — parent ka prop + locally deleted filter
  const rowsIndex = useMemo(() => {
    if (!locallyDeletedIds.size) return rowsIndexProp;
    const filtered = new Map();
    rowsIndexProp.forEach((children, key) => {
      filtered.set(
        key,
        (children || []).filter((c) => !locallyDeletedIds.has(c.id))
      );
    });
    return filtered;
  }, [rowsIndexProp, locallyDeletedIds]);

  // Jab parent rowsIndex update kare
  useEffect(() => {
    if (locallyDeletedIds.size > 0) {
      const timer = setTimeout(() => setLocallyDeletedIds(new Set()), 1500);
      return () => clearTimeout(timer);
    }
  }, [rowsIndexProp]); // eslint-disable-line react-hooks/exhaustive-deps

  const [ready, setReady] = useState(false);
  const [mapLoadError, setMapLoadError] = useState(null);
  const [hasPolygon, setHasPolygon] = useState(!!polygon?.length);
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [popup, setPopup] = useState(null);
  const [devicePopup, setDevicePopup] = useState(null);
  const [duplicatingDeviceId, setDuplicatingDeviceId] = useState(null);
  const [duplicateError, setDuplicateError] = useState(null);
  const [deletingDeviceId, setDeletingDeviceId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const [duplicateModal, setDuplicateModal] = useState(null);
  const [isAddingDeviceType, setIsAddingDeviceType] = useState(false);
  const [newDeviceTypeInput, setNewDeviceTypeInput] = useState('');
  const [customDeviceTypes, setCustomDeviceTypes] = useState([]);
  const skipNextDeviceTypeBlurRef = useRef(false);
  const [armedDeviceId, setArmedDeviceId] = useState(null);
  const armedDeviceIdRef = useRef(null);
  useEffect(() => { armedDeviceIdRef.current = armedDeviceId; }, [armedDeviceId]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawPointsCount, setDrawPointsCount] = useState(0);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [pendingDeviceCoords, setPendingDeviceCoords] = useState(row.level === 'DEVICE' ? { lat: row.lat, lng: row.lng } : null);
  const [childAction, setChildAction] = useState(null);
  const childActionRef = useRef(null);
  useEffect(() => { childActionRef.current = childAction; }, [childAction]);

  const [mainEditing, setMainEditing] = useState(false);
  const [hasDeviceMoves, setHasDeviceMoves] = useState(false);

  const [hierarchyLoading, setHierarchyLoading] = useState(false);
  const [hierarchyError, setHierarchyError] = useState(null);

  const [mapType, setMapType] = useState(() => {
    try {
      return localStorage.getItem('offStreetPolygonMapType') || 'roadmap';
    } catch {
      return 'roadmap';
    }
  });

  const isDevice = row.level === 'DEVICE';
  const color = LEVEL_COLORS[row.level] || '#6366F1';
  const shimmerMode = row.level === 'OPS' ? 'city' : row.level === 'LOCATION' || row.level === 'GATE' ? 'street' : 'overview';

  // Refs sync with state
  useEffect(() => { mapTypeRef.current = mapType; }, [mapType]);
  useEffect(() => { isDarkModeRef.current = isDarkMode; }, [isDarkMode]);

  // 👇 Map styles apply karne ka helper — mapType + labels visibility ke saath.
  // Roadmap → dark/light theme (+ optional labels off)
  // Hybrid  → sirf labels off (agar labels hidden hain), warna empty
  const applyMapStyles = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const hideLabels = !labelsVisibleRef.current;
    const styles = mapTypeRef.current === 'roadmap'
      ? (isDarkModeRef.current ? getDarkMapStyles(hideLabels) : getLightMapStyles(hideLabels))
      : getHybridMapStyles(hideLabels);
    map.setOptions({ styles });
  }, []);

  // 👇 Labels show/hide karne ka function — OffStreetMapPage jaisa
  const toggleLabels = useCallback(() => {
    labelsVisibleRef.current = !labelsVisibleRef.current;
    applyMapStyles();
  }, [applyMapStyles]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await ensureGoogleMapsLoaded();
      } catch (err) {
        if (!cancelled) setMapLoadError(err?.message || 'Failed to load Google Maps');
      }
      if (cancelled || !mapDivRef.current || !window.google?.maps) { setReady(true); return; }
      try {
        const google = window.google;
        mapRef.current = new google.maps.Map(mapDivRef.current, {
          center: { lat: 24.0, lng: 45.0 }, zoom: 6,
          mapTypeId: mapTypeRef.current,
          styles: mapTypeRef.current === 'roadmap'
            ? (isDarkModeRef.current
                ? getDarkMapStyles(!labelsVisibleRef.current)
                : getLightMapStyles(!labelsVisibleRef.current))
            : getHybridMapStyles(!labelsVisibleRef.current),
          mapTypeControl: false,
          streetViewControl: false, fullscreenControl: true,
        });

        const map = mapRef.current;

        // ── InfoWindow tight styling ──
        if (!document.getElementById('gm-infowindow-tight-style')) {
          const iwStyle = document.createElement('style');
          iwStyle.id = 'gm-infowindow-tight-style';
          iwStyle.textContent = `
            .gm-style-iw-c { padding: 10px 12px !important; }
            .gm-style-iw-d { overflow: visible !important; padding: 0 !important; }
            .gm-ui-hover-effect { top: 2px !important; right: 2px !important; }
          `;
          document.head.appendChild(iwStyle);
        }

        // ── View toggle + Labels animation styles ──
        if (!document.getElementById('gm-view-toggle-style')) {
          const styleTag = document.createElement('style');
          styleTag.id = 'gm-view-toggle-style';
          styleTag.textContent = `
            .gm-view-toggle-btn { transition: background 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.12s ease; }
            .gm-view-toggle-btn:hover:not(.gm-view-toggle-active) {
              background: ${isDarkModeRef.current ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.06)'} !important;
            }
            .gm-labels-popup {
              transition: opacity 0.25s ease, transform 0.25s ease;
            }
            .gm-labels-popup.gm-hidden {
              opacity: 0;
              transform: translateY(6px) scale(0.95);
              pointer-events: none;
            }
          `;
          document.head.appendChild(styleTag);
        }

        // ── Outer vertical stack ──
        const outerWrapper = document.createElement('div');
        outerWrapper.style.cssText = `
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          margin: 12px;
          font-family: 'Inter', Roboto, Arial, sans-serif; user-select: none;
        `;

        // ── Labels wrapper (initially HIDDEN) ──
        const labelsWrapper = document.createElement('div');
        labelsWrapper.className = 'gm-labels-popup gm-hidden';
        labelsWrapper.style.cssText = `
          display: flex; gap: 2px; padding: 3px;
          background: ${isDarkModeRef.current ? 'rgba(31,41,55,0.95)' : 'rgba(255,255,255,0.97)'};
          backdrop-filter: blur(6px);
          border: 1px solid ${isDarkModeRef.current ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'};
          border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          font-size: 12px;
        `;

        // ── Default / Satellite wrapper ──
        const wrapper = document.createElement('div');
        wrapper.style.cssText = `
          display: flex; gap: 2px; padding: 3px;
          background: ${isDarkModeRef.current ? 'rgba(31,41,55,0.95)' : 'rgba(255,255,255,0.97)'};
          backdrop-filter: blur(6px);
          border: 1px solid ${isDarkModeRef.current ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.06)'};
          border-radius: 999px; box-shadow: 0 2px 8px rgba(0,0,0,0.18);
          font-size: 13px;
        `;

        // ── Auto-hide timer for Labels button ──
        let labelsHideTimer = null;
        const showLabelsBtnTemporarily = () => {
          labelsWrapper.classList.remove('gm-hidden');
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
          labelsHideTimer = setTimeout(() => {
            labelsWrapper.classList.add('gm-hidden');
          }, 2500);
        };
        labelsHideTimerRef.current = labelsHideTimer;

        labelsWrapper.addEventListener('mouseenter', () => {
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
        });
        labelsWrapper.addEventListener('mouseleave', () => {
          if (labelsHideTimer) clearTimeout(labelsHideTimer);
          labelsHideTimer = setTimeout(() => {
            labelsWrapper.classList.add('gm-hidden');
          }, 1500);
        });

        // ── Default/Satellite button banao ──
        const makeBtn = (label, type) => {
          const btn = document.createElement('div');
          btn.textContent = label;
          btn.dataset.type = type;
          btn.className = 'gm-view-toggle-btn';
          btn.style.cssText = `
            padding: 7px 16px; cursor: pointer; white-space: nowrap; border-radius: 999px;
            font-weight: 500; letter-spacing: 0.01em;
            color: ${isDarkModeRef.current ? '#cbd5e1' : '#475569'};
          `;
          btn.addEventListener('click', () => {
            setMapType(type);
            // Default/Satellite click pe Labels button show karo
            showLabelsBtnTemporarily();
          });
          return btn;
        };

        const defaultBtn = makeBtn('Default View', 'roadmap');
        const satelliteBtn = makeBtn('Satellite View', 'hybrid');

        const updateActive = (activeType) => {
          [defaultBtn, satelliteBtn].forEach((btn) => {
            const isActive = btn.dataset.type === activeType;
            btn.classList.toggle('gm-view-toggle-active', isActive);
            btn.style.background = isActive ? 'var(--primary)' : 'transparent';
            btn.style.color = isActive
              ? 'var(--primary-foreground)'
              : isDarkModeRef.current
                ? '#cbd5e1'
                : '#475569';
            btn.style.fontWeight = isActive ? '600' : '500';
            btn.style.boxShadow = isActive ? '0 1px 4px rgba(73,127,253,0.4)' : 'none';
          });
        };
        updateActive(mapTypeRef.current);
        mapTypeToggleRef.current = updateActive;

        // ── Labels toggle button (initially hidden) ──
        const labelsBtn = document.createElement('div');
        labelsBtn.textContent = 'Labels';
        labelsBtn.className = 'gm-view-toggle-btn';
        labelsBtn.style.cssText = `
          padding: 5px 14px; cursor: pointer; white-space: nowrap; border-radius: 999px;
          font-weight: 500; letter-spacing: 0.01em;
          color: ${isDarkModeRef.current ? '#cbd5e1' : '#475569'};
        `;

        const updateLabelsBtnStyle = () => {
          const isOn = labelsVisibleRef.current;
          labelsBtn.classList.toggle('gm-view-toggle-active', isOn);
          labelsBtn.style.background = isOn ? 'var(--primary)' : 'transparent';
          labelsBtn.style.color = isOn
            ? 'var(--primary-foreground)'
            : isDarkModeRef.current
              ? '#cbd5e1'
              : '#475569';
          labelsBtn.style.fontWeight = isOn ? '600' : '500';
          labelsBtn.style.boxShadow = isOn ? '0 1px 4px rgba(73,127,253,0.4)' : 'none';
        };
        updateLabelsBtnStyle();

        labelsBtn.addEventListener('click', () => {
          toggleLabels();
          updateLabelsBtnStyle();
          showLabelsBtnTemporarily();
        });

        // ── Assemble ──
        labelsWrapper.appendChild(labelsBtn);
        wrapper.appendChild(defaultBtn);
        wrapper.appendChild(satelliteBtn);
        outerWrapper.appendChild(labelsWrapper);
        outerWrapper.appendChild(wrapper);

        map.controls[google.maps.ControlPosition.BOTTOM_CENTER].push(outerWrapper);
      } catch (err) {
        console.error('Failed to initialize map:', err);
        setMapLoadError(err?.message || 'Failed to initialize map');
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
      if (labelsHideTimerRef.current) clearTimeout(labelsHideTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 👇 mapType ya isDarkMode change hone par styles apply karo — applyMapStyles
  // khud hybrid ka case handle karta hai
  useEffect(() => {
    const map = mapRef.current;
    if (map) {
      map.setMapTypeId(mapType);
      applyMapStyles();
      mapTypeToggleRef.current?.(mapType);
    }
    try { localStorage.setItem('offStreetPolygonMapType', mapType); } catch { /* non-critical */ }
  }, [mapType, isDarkMode, applyMapStyles]);

  const attachPolygonEditListeners = useCallback((poly) => {
    const path = poly.getPath();
    ['insert_at', 'remove_at', 'set_at'].forEach((evt) => window.google.maps.event.addListener(path, evt, () => setDirty(true)));
  }, []);

  const handleChildClick = useCallback((child) => {
    if (isDrawingRef.current) return;
    if (childActionRef.current && childActionRef.current.mode !== 'draw') return;
    requestCloseRef.current(() => onJumpTo(child, child.level === 'DEVICE' ? 'coords' : undefined));
  }, [onJumpTo]);

  const handleMoveDevice = useCallback((device) => {
    pulledOutIdsRef.current.add(device.id);
    armedDeviceIdRef.current = device.id;
    setArmedDeviceId(device.id);
    setDevicePopup(null);
    renderChildrenRef.current();
  }, []);

  const cancelArmedMove = useCallback(() => {
    const id = armedDeviceIdRef.current;
    if (id != null) pulledOutIdsRef.current.delete(id);
    armedDeviceIdRef.current = null;
    setArmedDeviceId(null);
    renderChildrenRef.current();
  }, []);

  const openDuplicateModal = useCallback((device) => {
    const prefill = buildDuplicateDevicePayload(device);
    setDuplicateError(null);
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
    setDuplicateModal({
      mode: 'duplicate',
      device,
      row_id: prefill.row_id ?? '',
      device_id: '',
      device_name: prefill.device_name ?? '',
      device_type: prefill.device_type ?? '',
      latitude: prefill.latitude ?? '',
      longitude: prefill.longitude ?? '',
    });
  }, []);

  const openCreateModal = useCallback((device) => {
    setDuplicateError(null);
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
    setDuplicateModal({
      mode: 'create',
      device,
      row_id: '',
      device_id: '',
      device_name: '',
      device_type: '',
      latitude: device?.lat != null ? String(device.lat) : '',
      longitude: device?.lng != null ? String(device.lng) : '',
    });
  }, []);

  const commitNewDeviceType = useCallback(() => {
    const newType = newDeviceTypeInput.trim();
    if (newType) {
      setCustomDeviceTypes((p) => (p.includes(newType) ? p : [...p, newType]));
      setDuplicateModal((p) => (p ? { ...p, device_type: newType } : p));
    }
    setIsAddingDeviceType(false);
    setNewDeviceTypeInput('');
  }, [newDeviceTypeInput]);

  const deviceTypeOptions = useMemo(() => {
    const types = new Set();
    rowsIndex?.forEach?.((children) => {
      (children || []).forEach((r) => {
        if (r.level !== 'DEVICE') return;
        const t = r.deviceType ?? r.raw?.device_type;
        if (t) types.add(t);
      });
    });
    customDeviceTypes.forEach((t) => types.add(t));
    return Array.from(types).sort((a, b) => a.localeCompare(b));
  }, [rowsIndex, customDeviceTypes]);

  const handleDuplicateSubmit = useCallback(async () => {
    if (!duplicateModal) return;
    const { device, ...form } = duplicateModal;
    if (!form.device_id?.trim()) { setDuplicateError('Device ID is required.'); return; }
    setDuplicatingDeviceId(device.id);
    setDuplicateError(null);
    try {
      const payload = {
        row_id: form.row_id,
        device_id: form.device_id.trim(),
        device_name: form.device_name,
        device_type: form.device_type,
        latitude: form.latitude,
        longitude: form.longitude,
      };
      const duplicated = await duplicateDeviceApi(payload);
      setDuplicateModal(null);
      await onDeviceDuplicated?.(device, duplicated);
    } catch (err) {
      setDuplicateError(err?.message || 'Failed to duplicate device');
    } finally {
      setDuplicatingDeviceId(null);
    }
  }, [duplicateModal, onDeviceDuplicated]);

  const handleCreateSubmit = useCallback(async () => {
    if (!duplicateModal) return;
    const form = duplicateModal;
    if (!form.device_id?.trim()) { setDuplicateError('Device ID is required.'); return; }
    setDuplicatingDeviceId(CREATE_DEVICE_ID);
    setDuplicateError(null);
    try {
      const payload = {
        ...buildHierarchyFields(row),
        device_id: form.device_id.trim(),
        device_name: form.device_name,
        device_type: form.device_type,
        latitude: form.latitude,
        longitude: form.longitude,
      };
      const created = await createDeviceApi(payload);
      setDuplicateModal(null);
      await onDeviceCreated?.(created);
    } catch (err) {
      setDuplicateError(err?.message || 'Failed to create device');
    } finally {
      setDuplicatingDeviceId(null);
    }
  }, [duplicateModal, onDeviceCreated, row]);

  const handleDeleteDevice = useCallback((device) => {
    const rowId = device.raw?.row_id ?? device.rowId ?? device.id;
    setDeleteError(null);
    setDeleteConfirm({ device, rowId });
  }, []);

  const confirmDeleteDevice = useCallback(async () => {
    if (!deleteConfirm) return;
    const { device, rowId } = deleteConfirm;
    setDeletingDeviceId(device.id);
    setDeleteError(null);
    try {
      await deleteDeviceApi(rowId);

      setLocallyDeletedIds((prev) => {
        const next = new Set(prev);
        next.add(device.id);
        return next;
      });
      deviceOverridesRef.current.delete(device.id);
      pulledOutIdsRef.current.delete(device.id);
      setArmedDeviceId(null);
      armedDeviceIdRef.current = null;

      setDeleteConfirm(null);
      setDevicePopup(null);

      await onDeviceDeleted?.(device);
        console.log('🗑️ onDeviceDeleted called with:', device);

      renderChildrenRef.current();
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete device');
      setDeleteConfirm(null);
    } finally {
      setDeletingDeviceId(null);
    }
  }, [deleteConfirm, onDeviceDeleted]);

  const openApiNodePopup = useCallback((apiRow) => {
    if (isDrawingRef.current) return;
    if (childActionRef.current && childActionRef.current.mode !== 'draw') return;
    setPopup({ name: apiRow.name, level: apiRow.level, code: apiRow.code, childRow: apiRow, hasPolygon: !!apiRow.raw?.polygon?.length, isApiNode: true });
  }, []);

  const cleanupTempDraw = useCallback(() => {
    tempMarkersRef.current.forEach((m) => m.setMap(null)); tempMarkersRef.current = [];
    if (tempPolygonRef.current) { tempPolygonRef.current.setMap(null); tempPolygonRef.current = null; }
    if (tempShadowRef.current) { clearShadow(tempShadowRef.current); tempShadowRef.current = null; }
    drawPointsRef.current = [];
    setDrawPointsCount(0);
  }, []);

  const startDraw = () => {
    if (!mapRef.current || isDrawingRef.current || childAction) return;
    if (mainEditing) cancelMainEdit();
    if (activePolygonRef.current) {
      activePolygonRef.current.setMap(null);
      activePolygonRef.current = null;
      setHasPolygon(false);
    }
    if (activeShadowRef.current) {
      clearShadow(activeShadowRef.current);
      activeShadowRef.current = null;
    }
    setDirty(true);
    cleanupTempDraw();
    drawTargetRef.current = row;
    isDrawingRef.current = true;
    setIsDrawing(true);
  };

  const cancelDraw = () => {
    cleanupTempDraw();
    isDrawingRef.current = false;
    setIsDrawing(false);
    drawTargetRef.current = row;
    if (childActionRef.current?.mode === 'draw') setChildAction(null);
  };

  const startChildDraw = (child) => {
    if (isDrawingRef.current || childAction) return;
    setPopup(null);
    cleanupTempDraw();
    drawTargetRef.current = child;
    isDrawingRef.current = true;
    setIsDrawing(true);
    setChildAction({ child, mode: 'draw' });
  };

  const beginEditPolygon = (child, polygonInstance) => {
    if (isDrawingRef.current || childAction || !polygonInstance) return;
    setPopup(null);
    childOriginalPathRef.current = polygonInstance.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    polygonInstance.setOptions({ editable: true, fillOpacity: 0.30, zIndex: 5 });
    activeChildPolygonRef.current = polygonInstance;
    setChildAction({ child, mode: 'edit' });
  };

  const startChildEdit = (child) => {
    const entry = childPolygonsRef.current.get(child.id);
    beginEditPolygon(child, entry?.polygon);
  };

  const startApiNodeEdit = (child) => {
    const entry = apiPolygonsMapRef.current.get(child.id);
    beginEditPolygon(child, entry?.polygon);
  };

  const cancelChildEdit = () => {
    if (activeChildPolygonRef.current && childOriginalPathRef.current) {
      activeChildPolygonRef.current.setPath(childOriginalPathRef.current);
      activeChildPolygonRef.current.setOptions({ editable: false, fillOpacity: 0.22, zIndex: 1 });
    }
    activeChildPolygonRef.current = null;
    childOriginalPathRef.current = null;
    setChildAction(null);
  };

  const saveChildEdit = async () => {
    if (!activeChildPolygonRef.current || !childActionRef.current) return;
    const child = childActionRef.current.child;
    const coordinates = activeChildPolygonRef.current.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    setChildAction({ child, mode: 'saving' });
    try {
      await savePolygonToApi(child, coordinates);
      activeChildPolygonRef.current.setOptions({ editable: false, fillOpacity: 0.22, zIndex: 1 });
      activeChildPolygonRef.current = null;
      childOriginalPathRef.current = null;
      setChildAction(null);
      if (child.isApiNode) {
        loadHierarchyPolygons();
      } else {
        onChildPolygonSaved?.(child);
        loadHierarchyPolygons();
      }
    } catch (err) {
      alert(err?.message || 'Failed to save polygon');
      setChildAction({ child, mode: 'edit' });
    }
  };

  const startMainEdit = () => {
    if (!activePolygonRef.current || isDrawingRef.current || isDrawing || childAction) return;
    mainOriginalPathRef.current = activePolygonRef.current.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
    activePolygonRef.current.setOptions({ editable: true, fillOpacity: 0.30, zIndex: 5 });
    setMainEditing(true);
  };

  const cancelMainEdit = () => {
    if (activePolygonRef.current && mainOriginalPathRef.current) {
      activePolygonRef.current.setPath(mainOriginalPathRef.current);
    }
    activePolygonRef.current?.setOptions({ editable: false, fillOpacity: 0.16, zIndex: 1 });
    mainOriginalPathRef.current = null;
    setMainEditing(false);
    setDirty(false);
  };

  const renderChildren = useCallback((bounds) => {
    const google = window.google;
    const map = mapRef.current;
    if (!google || !map) return;

    childMarkersRef.current.forEach((m) => m.setMap(null)); childMarkersRef.current = [];
    childPolygonsRef.current.forEach((entry) => {
      entry.polygon?.setMap(null);
      clearShadow(entry.shadow);
    });
    childPolygonsRef.current = new Map();
    labelDeclutterRef.current.reset();

    const directChildren = rowsIndex.get(row.id) || [];
    const isGateLevel = row.level === 'GATE';

    if (isGateLevel) {
      const getEffectiveCoord = (device) => {
        const override = deviceOverridesRef.current.get(device.id);
        return override ? { lat: override.lat, lng: override.lng } : { lat: device.lat, lng: device.lng };
      };
      const attachDragHandler = (marker, device) => {
        marker.addListener('dragend', (e) => {
          deviceOverridesRef.current.set(device.id, { device, lat: e.latLng.lat(), lng: e.latLng.lng() });
          pulledOutIdsRef.current.delete(device.id);
          armedDeviceIdRef.current = null;
          setArmedDeviceId(null);
          setHasDeviceMoves(true);
          setDirty(true);
          renderChildrenRef.current();
        });
      };
      const openDevicePopupAt = (devices, e, lat, lng) => {
        e?.domEvent?.preventDefault?.();
        if (isDrawingRef.current) return;
        if (childActionRef.current && childActionRef.current.mode !== 'draw') return;
        const rect = mapDivRef.current?.getBoundingClientRect();
        const clientX = e?.domEvent?.clientX;
        const clientY = e?.domEvent?.clientY;
        const pos = (clientX != null && clientY != null && rect)
          ? { x: clientX - rect.left, y: clientY - rect.top }
          : null;
        setDevicePopup({ lat, lng, devices, pos });
      };
      const attachPopupHandlers = (marker, devices, lat, lng) => {
        marker.addListener('click', (e) => openDevicePopupAt(devices, e, lat, lng));
      };

      const allDevices = collectDirectChildDevices(row, rowsIndex).filter((d) => d.lat != null && d.lng != null);
      const pulledOut = allDevices.filter((d) => pulledOutIdsRef.current.has(d.id));
      const pulledOutIds = new Set(pulledOut.map((d) => d.id));
      const grouped = allDevices.filter((d) => !pulledOutIds.has(d.id));

      pulledOut.forEach((device) => {
        const { lat, lng } = getEffectiveCoord(device);
        const isArmed = armedDeviceIdRef.current === device.id;
        const icon = buildMarkerIcon(google, device.deviceType, getDeviceColor(device.deviceType), true, isArmed);
        const marker = new google.maps.Marker({
          position: { lat, lng }, map, icon, draggable: isArmed, zIndex: isArmed ? 999 : 10,
          animation: isArmed ? google.maps.Animation.BOUNCE : null,
          title: `${device.name} (${getDeviceTypeName(device.deviceType)}) — click for options`,
        });
        attachDragHandler(marker, device);
        attachPopupHandlers(marker, [device], lat, lng);
        childMarkersRef.current.push(marker);
        bounds?.extend({ lat, lng });
      });

      const deviceGroups = groupDevicesByCoord(grouped.map((d) => ({ ...d, ...getEffectiveCoord(d) })));
      deviceGroups.forEach((group) => {
        const { lat, lng } = group[0];
        if (group.length === 1) {
          const device = group[0];
          const isArmed = armedDeviceIdRef.current === device.id;
          const icon = buildMarkerIcon(google, device.deviceType, getDeviceColor(device.deviceType), false, isArmed);
          const marker = new google.maps.Marker({
            position: { lat, lng }, map, icon, draggable: isArmed, zIndex: isArmed ? 999 : undefined,
            animation: isArmed ? google.maps.Animation.BOUNCE : null,
            title: `${device.name} (${getDeviceTypeName(device.deviceType)}) — click for options`,
          });
          attachDragHandler(marker, device);
          attachPopupHandlers(marker, [device], lat, lng);
          childMarkersRef.current.push(marker);
        } else {
          const icon = buildClusterMarkerIcon(google, group.length, getDeviceColor(group[0].deviceType));
          const marker = new google.maps.Marker({ position: { lat, lng }, map, icon, title: `${group.length} devices at this location — click for options` });
          attachPopupHandlers(marker, group, lat, lng);
          childMarkersRef.current.push(marker);
        }
        bounds?.extend({ lat, lng });
      });
      return;
    }

    directChildren.forEach((child) => {
      const childColor = LEVEL_COLORS[child.level] || '#6366F1';
      const childPolyCoords = normalizePolygonCoords(child.raw?.polygon);
      let polyObj = null;
      let shadowObj = null;
      let lat, lng;

      if (childPolyCoords?.length) {
        const { polygon: p, shadow: s } = createThickPolygon(google, childPolyCoords, childColor, map, {
          clickable: true, editable: false, zIndex: 1,
        });
        polyObj = p;
        shadowObj = s;
        polyObj.addListener('click', () => handleChildClick(child));
        childPolyCoords.forEach((p) => bounds?.extend(p));
        ({ lat, lng } = polygonCentroid(childPolyCoords));
      } else {
        const pts = collectDescendantPoints(child, rowsIndex);
        if (!pts.length) { childPolygonsRef.current.set(child.id, { polygon: null, shadow: null, marker: null }); return; }
        ({ lat, lng } = polygonCentroid(pts));
      }

      const labelPos = labelDeclutterRef.current.place(lat, lng);
      const icon = buildEntityLabelIcon(google, `${child.name} (${child.childCount ?? 0})`, childColor);
      const marker = new google.maps.Marker({ position: labelPos, map, icon, title: child.name });
      marker.addListener('click', () => handleChildClick(child));
      childMarkersRef.current.push(marker);
      childPolygonsRef.current.set(child.id, { polygon: polyObj, shadow: shadowObj, marker });
    });
  }, [row, rowsIndex, handleChildClick]);
  useEffect(() => { renderChildrenRef.current = renderChildren; }, [renderChildren]);

  useEffect(() => {
    if (!ready || !mapRef.current || isDevice) return;
    renderChildrenRef.current();
  }, [locallyDeletedIds, ready, isDevice]);

  useEffect(() => {
    if (!ready || !mapRef.current || isDevice) return;
    renderChildrenRef.current();
  }, [armedDeviceId, ready, isDevice]);

  const clearApiPolygons = useCallback(() => {
    apiPolygonsMapRef.current.forEach((entry) => {
      entry.polygon?.setMap(null);
      clearShadow(entry.shadow);
    });
    apiPolygonsMapRef.current = new Map();
  }, []);

  const loadHierarchyPolygons = useCallback(async (bounds) => {
    if (isDevice) return;
    const myGen = ++hierarchyFetchGenRef.current;
    clearApiPolygons();
    setHierarchyError(null);
    setHierarchyLoading(true);
    try {
      const tree = await fetchPolygonHierarchy(row);
      if (myGen !== hierarchyFetchGenRef.current) return;
      const google = window.google;
      const map = mapRef.current;
      if (!google || !map) return;

      const fitBounds = new google.maps.LatLngBounds();
      if (bounds && !bounds.isEmpty?.()) {
        fitBounds.union(bounds);
      }

      renderChildren(fitBounds);

      const walk = (nodes, depth) => {
        (nodes || []).forEach((node) => {
          if (depth === 0) {
            const nodeCoords = normalizePolygonCoords(node.coordinates);
            if (nodeCoords.length && !activePolygonRef.current) {
              const { polygon: poly, shadow } = createThickPolygon(google, nodeCoords, color, map, {
                editable: false, clickable: true,
              });
              activePolygonRef.current = poly;
              activeShadowRef.current = shadow;
              attachPolygonEditListeners(poly);
              setHasPolygon(true);
              nodeCoords.forEach((p) => fitBounds.extend(p));
            }
          }
        });
      };
      walk(tree, 0);

      if (!fitBounds.isEmpty()) {
        map.fitBounds(fitBounds, 60);
        google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
          if (map.getZoom() > 17) map.setZoom(17);
        });
      }
    } catch (err) {
      if (myGen === hierarchyFetchGenRef.current) setHierarchyError(err?.message || 'Failed to load polygon hierarchy');
    } finally {
      if (myGen === hierarchyFetchGenRef.current) setHierarchyLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [row, rowsIndex, isDevice, clearApiPolygons, openApiNodePopup, handleChildClick, renderChildren, color, attachPolygonEditListeners]);

  useEffect(() => {
    if (!ready || !mapRef.current) return;
    const google = window.google;
    const map = mapRef.current;

    if (activePolygonRef.current) { activePolygonRef.current.setMap(null); activePolygonRef.current = null; }
    if (activeShadowRef.current) { clearShadow(activeShadowRef.current); activeShadowRef.current = null; }
    childMarkersRef.current.forEach((m) => m.setMap(null)); childMarkersRef.current = [];
    childPolygonsRef.current.forEach((entry) => {
      entry.polygon?.setMap(null);
      clearShadow(entry.shadow);
    });
    childPolygonsRef.current = new Map();
    clearApiPolygons();
    if (deviceMarkerRef.current) { deviceMarkerRef.current.setMap(null); deviceMarkerRef.current = null; }
    google.maps.event.clearListeners(map, 'click');
    google.maps.event.clearListeners(map, 'dblclick');
    tempMarkersRef.current.forEach((m) => m.setMap(null)); tempMarkersRef.current = [];
    if (tempPolygonRef.current) { tempPolygonRef.current.setMap(null); tempPolygonRef.current = null; }
    if (tempShadowRef.current) { clearShadow(tempShadowRef.current); tempShadowRef.current = null; }
    drawPointsRef.current = []; isDrawingRef.current = false;
    activeChildPolygonRef.current = null; childOriginalPathRef.current = null;
    mainOriginalPathRef.current = null;
    drawTargetRef.current = row;
    deviceOverridesRef.current = new Map();
    pulledOutIdsRef.current = new Set();
    setIsDrawing(false); setDrawPointsCount(0);
    setDirty(false); setPopup(null); setDevicePopup(null); setSaveError(null); setChildAction(null);
    setDuplicatingDeviceId(null); setDuplicateError(null);
    setDuplicateModal(null); setArmedDeviceId(null); armedDeviceIdRef.current = null;
    setMainEditing(false); setHasDeviceMoves(false);
    setHierarchyError(null); setHierarchyLoading(false);
    setDeleteConfirm(null); setDeleteError(null); setDeletingDeviceId(null);
    setLocallyDeletedIds(new Set());

    const bounds = new google.maps.LatLngBounds();

    if (isDevice) {
      const lat = pendingDeviceCoords?.lat ?? row.lat;
      const lng = pendingDeviceCoords?.lng ?? row.lng;
      if (lat != null && lng != null) {
        const marker = new google.maps.Marker({ position: { lat, lng }, map, draggable: true, icon: buildMarkerIcon(google, row.raw?.device_type, getDeviceColor(row.raw?.device_type), true) });
        marker.addListener('dragend', (e) => { setPendingDeviceCoords({ lat: e.latLng.lat(), lng: e.latLng.lng() }); setDirty(true); });
        deviceMarkerRef.current = marker;
        bounds.extend({ lat, lng });
      }
      map.addListener('click', (e) => {
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        if (deviceMarkerRef.current) { deviceMarkerRef.current.setPosition(pos); }
        else {
          const marker = new google.maps.Marker({ position: pos, map, draggable: true, icon: buildMarkerIcon(google, row.raw?.device_type, getDeviceColor(row.raw?.device_type), true) });
          marker.addListener('dragend', (ev) => { setPendingDeviceCoords({ lat: ev.latLng.lat(), lng: ev.latLng.lng() }); setDirty(true); });
          deviceMarkerRef.current = marker;
        }
        setPendingDeviceCoords(pos); setDirty(true);
      });
    } else {
      const initialCoords = normalizePolygonCoords(polygon);
      if (initialCoords.length) {
        const { polygon: poly, shadow } = createThickPolygon(google, initialCoords, color, map, {
          editable: false, clickable: true,
        });
        activePolygonRef.current = poly;
        activeShadowRef.current = shadow;
        attachPolygonEditListeners(poly);
        setHasPolygon(true);
        initialCoords.forEach((p) => bounds.extend(p));
      } else setHasPolygon(false);

      map.addListener('click', (e) => {
        setDevicePopup(null);
        if (armedDeviceIdRef.current != null) { armedDeviceIdRef.current = null; setArmedDeviceId(null); }
        if (!isDrawingRef.current) return;
        if (drawPointsRef.current.length >= POLYGON_CORNER_COUNT) return;
        const pos = { lat: e.latLng.lat(), lng: e.latLng.lng() };
        drawPointsRef.current = [...drawPointsRef.current, pos];
        setDrawPointsCount(drawPointsRef.current.length);
        const dot = new google.maps.Marker({
          position: pos, map,
          icon: { path: google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: color, fillOpacity: 1, strokeColor: '#ffffff', strokeWeight: 1.5 },
        });
        tempMarkersRef.current.push(dot);
        if (!tempPolygonRef.current) {
          const { polygon: tmpPoly, shadow: tmpShadow } = createThickPolygon(google, drawPointsRef.current, color, map, {
            clickable: false, editable: false,
          });
          tempPolygonRef.current = tmpPoly;
          tempShadowRef.current = tmpShadow;
        } else {
          tempPolygonRef.current.setPath(drawPointsRef.current);
          setShadowPath(tempShadowRef.current, drawPointsRef.current);
        }
        if (drawPointsRef.current.length === POLYGON_CORNER_COUNT) finishDrawRef.current?.();
      });
      map.addListener('dblclick', () => { if (isDrawingRef.current) finishDrawRef.current?.(); });

      loadHierarchyPolygons(bounds);
    }

    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 60);
      google.maps.event.addListenerOnce(map, 'bounds_changed', () => { if (map.getZoom() > 17) map.setZoom(17); });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, row.id]);

  useEffect(() => {
    if (!ready || !mapRef.current || isDevice) return;
    if (childActionRef.current && childActionRef.current.mode !== 'draw') return;
    loadHierarchyPolygons();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsIndexProp]);

  const finishDraw = useCallback(async () => {
    if (drawPointsRef.current.length < POLYGON_CORNER_COUNT) { alert(`Polygon banane ke liye ${POLYGON_CORNER_COUNT} points chahiye (square).`); return; }
    const google = window.google;
    const paths = drawPointsRef.current;
    const target = drawTargetRef.current;
    cleanupTempDraw();
    isDrawingRef.current = false;
    setIsDrawing(false);

    if (target && target.id !== row.id) {
      setChildAction({ child: target, mode: 'saving' });
      try {
        await savePolygonToApi(target, paths);
        if (target.isApiNode) {
          loadHierarchyPolygons();
        } else {
          onChildPolygonSaved?.(target);
          loadHierarchyPolygons();
        }
      } catch (err) {
        alert(err?.message || 'Failed to save polygon');
      } finally {
        setChildAction(null);
        drawTargetRef.current = row;
      }
      return;
    }

    const { polygon: poly, shadow } = createThickPolygon(google, paths, color, mapRef.current, {
      editable: true, clickable: true,
    });
    activePolygonRef.current = poly;
    activeShadowRef.current = shadow;
    attachPolygonEditListeners(poly);
    setHasPolygon(true); setDirty(true); setMainEditing(true);

    if (mapRef.current) {
      const bounds = new google.maps.LatLngBounds();
      paths.forEach((p) => bounds.extend(p));
      mapRef.current.fitBounds(bounds, 80);
      google.maps.event.addListenerOnce(mapRef.current, 'bounds_changed', () => {
        if (mapRef.current.getZoom() > 18) mapRef.current.setZoom(18);
      });
    }
  }, [color, attachPolygonEditListeners, cleanupTempDraw, row, onChildPolygonSaved, loadHierarchyPolygons]);

  const finishDrawRef = useRef(finishDraw);
  useEffect(() => { finishDrawRef.current = finishDraw; }, [finishDraw]);

  const fitToBounds = () => {
    if (!mapRef.current) return;
    const google = window.google;
    const map = mapRef.current;
    const bounds = new google.maps.LatLngBounds();
    let any = false;
    if (activePolygonRef.current) { activePolygonRef.current.getPath().forEach((ll) => { bounds.extend(ll); any = true; }); }
    childMarkersRef.current.forEach((m) => { bounds.extend(m.getPosition()); any = true; });
    childPolygonsRef.current.forEach((entry) => { entry.polygon?.getPath().forEach((ll) => { bounds.extend(ll); any = true; }); });
    apiPolygonsMapRef.current.forEach((entry) => { entry.polygon?.getPath().forEach((ll) => { bounds.extend(ll); any = true; }); });
    if (deviceMarkerRef.current) { bounds.extend(deviceMarkerRef.current.getPosition()); any = true; }
    if (!any) return;
    map.fitBounds(bounds, 60);
    google.maps.event.addListenerOnce(map, 'bounds_changed', () => {
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) map.setZoom(17);
      else if (map.getZoom() > 17) map.setZoom(17);
    });
  };

  const handleSaveClick = async () => {
    if (isDevice) {
      if (!pendingDeviceCoords) return;
      onDeviceSave?.(row, pendingDeviceCoords);
      setDirty(false);
      return;
    }

    const hasPolygonChange = hasPolygon && !!activePolygonRef.current && (mainEditing || isDrawing);
    const deviceMoves = Array.from(deviceOverridesRef.current.values());
    if (!hasPolygonChange && !deviceMoves.length) return;

    setSaving(true); setSaveError(null);
    try {
      if (hasPolygonChange) {
        const coordinates = activePolygonRef.current.getPath().getArray().map((ll) => ({ lat: ll.lat(), lng: ll.lng() }));
        await savePolygonToApi(row, coordinates);
        activePolygonRef.current?.setOptions({ editable: false, fillOpacity: 0.16, zIndex: 1 });
        mainOriginalPathRef.current = null;
        setMainEditing(false);
      }
      if (deviceMoves.length) {
        await onDevicesCoordsSave?.(deviceMoves.map(({ device, lat, lng }) => ({ row: device, coords: { lat, lng } })));
        deviceOverridesRef.current = new Map();
        pulledOutIdsRef.current = new Set();
        setHasDeviceMoves(false);
        renderChildren();
      }
      setDirty(false);
      onPolygonSaved?.(row);
    } catch (err) {
      setSaveError(err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const requestClose = (fn) => {
    if (isDrawingRef.current) cancelDraw();
    if (childActionRef.current?.mode === 'edit') cancelChildEdit();
    if (mainEditing) cancelMainEdit();
    if (dirty) setConfirmDiscard(() => fn); else fn();
  };
  useEffect(() => { requestCloseRef.current = requestClose; });
  const iconBtn = (active) => `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${active ? 'bg-primary text-primary-foreground border-primary' : 'bg-card border-border text-foreground hover:bg-secondary'}`;

  const childDrawTargetName = isDrawing && drawTargetRef.current && drawTargetRef.current.id !== row.id ? drawTargetRef.current.name : null;

  return (
    <div className="fixed inset-0 z-[200] bg-background flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-card">
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground flex-wrap">
            {breadcrumb.map((c, i) => (
              <span key={c.id} className="flex items-center gap-1">
                <button className="hover:text-primary hover:underline font-medium" onClick={() => requestClose(() => onBreadcrumbClick(c))}>{c.name}</button>
                {i < breadcrumb.length - 1 && <ChevronRight className="w-3 h-3" />}
              </span>
            ))}
          </div>
          <h2 className="text-sm font-bold text-foreground truncate">{isDevice ? 'Update Coordinates' : 'Manage Polygon'} — {row.name}</h2>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-card/60 flex-wrap">
        {!isDevice && (
          <>
            {!isDrawing ? (
              <button
                className={iconBtn(false)}
                onClick={startDraw}
                disabled={!!childAction}
                title={hasPolygon ? 'This will replace the existing polygon' : undefined}
              >
                <Pentagon className="w-3.5 h-3.5" /> Draw Polygon
              </button>
            ) : (
              <button className={iconBtn(true)} onClick={cancelDraw}>
                <X className="w-3.5 h-3.5" /> Cancel Draw{childDrawTargetName ? ` — ${childDrawTargetName}` : ''} ({drawPointsCount}/{POLYGON_CORNER_COUNT})
              </button>
            )}

            {hasPolygon && !isDrawing && !childAction && (
              !mainEditing ? (
                <button className={iconBtn(false)} onClick={startMainEdit}>
                  <Pencil className="w-3.5 h-3.5" /> Edit Polygon
                </button>
              ) : (
                <button className={iconBtn(true)} onClick={cancelMainEdit}>
                  <X className="w-3.5 h-3.5" /> Cancel Edit
                </button>
              )
            )}

            <div className="w-px h-5 bg-border mx-1" />
          </>
        )}
        <button className={iconBtn(false)} onClick={fitToBounds}><Maximize2 className="w-3.5 h-3.5" /> Fit to Bounds</button>
        {row.level === 'GATE' && (
          <button
            className={iconBtn(false)}
            onClick={() => openCreateModal(collectDirectChildDevices(row, rowsIndex).find((d) => d.lat != null && d.lng != null) || null)}
          >
            <Plus className="w-3.5 h-3.5" /> Add Device
          </button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button className="px-3 py-1.5 rounded-lg text-xs font-semibold border border-border text-muted-foreground hover:bg-secondary" onClick={() => requestClose(onClose)}>Cancel</button>
          <button
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
            onClick={handleSaveClick}
            disabled={!dirty || saving || !!childAction || (!isDevice && !isDrawing && !mainEditing && !hasDeviceMoves)}
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {saving ? 'Saving...' : (isDevice ? 'Save Coordinates' : 'Save Changes')}
          </button>
        </div>
      </div>
      </div>

      {saveError && (
        <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-500 text-xs">{saveError}</div>
      )}
      {deleteError && (
        <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-500 text-xs flex items-center justify-between gap-2">
          <span>{deleteError}</span>
          <button className="underline font-semibold shrink-0" onClick={() => setDeleteError(null)}>Dismiss</button>
        </div>
      )}
      {duplicateError && (
        <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-500 text-xs flex items-center justify-between gap-2">
          <span>{duplicateError}</span>
          <button className="underline font-semibold shrink-0" onClick={() => setDuplicateError(null)}>Dismiss</button>
        </div>
      )}
      {hierarchyError && (
        <div className="px-4 py-1.5 bg-red-500/10 border-b border-red-500/20 text-red-500 text-xs flex items-center justify-between gap-2">
          <span>Couldn't load full hierarchy: {hierarchyError}</span>
          <button className="underline font-semibold shrink-0" onClick={() => loadHierarchyPolygons()}>Retry</button>
        </div>
      )}

      <div className="flex-1 relative">
        <div ref={mapDivRef} className="w-full h-full" />
        {!ready && <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm text-muted-foreground">Loading map…</div>}
        {ready && !mapLoadError && (
          <OffStreetPolygonEditShimmer visible={hierarchyLoading} isDarkMode={isDarkMode} mode={shimmerMode} />
        )}
        {ready && mapLoadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/90 p-6 text-center">
            <div className="max-w-sm">
              <p className="text-sm font-semibold text-red-500 mb-1">Map failed to load</p>
              <p className="text-xs text-muted-foreground">{mapLoadError}</p>
            </div>
          </div>
        )}
        {ready && !mapLoadError && !isDevice && isDrawing && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-primary/10 border border-primary/30 text-primary rounded-lg px-3 py-1.5 text-xs shadow">
            {childDrawTargetName
              ? `Drawing a new polygon for "${childDrawTargetName}" — click each corner (${drawPointsCount}/${POLYGON_CORNER_COUNT}), saves automatically on the ${POLYGON_CORNER_COUNT}th.`
              : `Click on the map to place each corner (${drawPointsCount}/${POLYGON_CORNER_COUNT}) — the square finishes automatically after the ${POLYGON_CORNER_COUNT}th point.`}
          </div>
        )}
        {ready && !mapLoadError && !isDevice && mainEditing && !isDrawing && !childAction && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-primary/10 border border-primary/30 text-primary rounded-lg px-3 py-1.5 text-xs shadow">
            Editing polygon for <strong>{row.name}</strong> — drag the corners, then Save Changes (or Cancel Edit to discard).
          </div>
        )}
        {ready && !mapLoadError && !isDevice && row.level === 'GATE' && hasPolygon && !isDrawing && !mainEditing && !childAction && !armedDeviceId && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-muted-foreground shadow">
            Drag any device to reposition it — for devices stacked at the same spot, open the cluster marker and pick one to drag. Then Save Changes.
          </div>
        )}
        {ready && !mapLoadError && !isDevice && row.level === 'GATE' && armedDeviceId && !isDrawing && !childAction && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-2 text-xs shadow flex items-center gap-2 z-20">
            <span className="text-foreground">
              Ready to move <strong>{collectDirectChildDevices(row, rowsIndex).find((d) => d.id === armedDeviceId)?.name || 'device'}</strong> — drag it to its new position.
            </span>
            <button className="px-2 py-1 rounded-md border border-border text-foreground hover:bg-secondary" onClick={cancelArmedMove}>Cancel</button>
          </div>
        )}
        {ready && !mapLoadError && !isDevice && childAction && childAction.mode !== 'draw' && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-2 text-xs shadow flex items-center gap-2 z-20">
            {childAction.mode === 'saving' ? (
              <span className="flex items-center gap-1.5 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving polygon for "{childAction.child.name}"…</span>
            ) : (
              <>
                <span className="text-foreground">Editing polygon for <strong>{childAction.child.name}</strong> — drag the corners</span>
                <button className="px-2 py-1 rounded-md bg-primary text-primary-foreground font-bold" onClick={saveChildEdit}>Save</button>
                <button className="px-2 py-1 rounded-md border border-border text-foreground hover:bg-secondary" onClick={cancelChildEdit}>Cancel</button>
              </>
            )}
          </div>
        )}
        {ready && !mapLoadError && !isDevice && !hasPolygon && !isDrawing && !childAction && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-card border border-border rounded-lg px-3 py-1.5 text-xs text-muted-foreground shadow">
            {row.level === 'GATE'
              ? 'No polygon set yet — click "Draw Polygon" above to create one. Devices below can be dragged directly to reposition them.'
              : 'No polygon set yet — click "Draw Polygon" above to create one, or click a child/descendant shape or marker below to manage its polygon.'}
          </div>
        )}
        {devicePopup && (
          <>
          <div className="fixed inset-0 z-20" onClick={() => setDevicePopup(null)} />
          <div
            className="absolute z-30 bg-card border border-border rounded-xl shadow-xl p-3 w-64 max-h-72 overflow-y-auto"
            style={devicePopup.pos ? { left: devicePopup.pos.x, top: devicePopup.pos.y } : { bottom: 16, left: 16 }}
          >
            <div className="flex items-start justify-between mb-2">
              <p className="text-xs font-bold text-foreground">{devicePopup.devices.length} devices at this location</p>
              <button onClick={() => setDevicePopup(null)} className="text-muted-foreground hover:text-foreground flex-shrink-0"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="space-y-1">
              {devicePopup.devices.map((device) => (
                <div key={device.id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-secondary transition-colors">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: getDeviceColor(device.deviceType) }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-xs font-semibold text-foreground truncate">{device.name}</span>
                    <span className="block text-[10px] text-muted-foreground truncate">
                      {getDeviceTypeName(device.deviceType)}
                      {(device.code || device.raw?.device_id) ? ` · ID: ${device.code || device.raw?.device_id}` : ''}
                    </span>
                  </span>
                  <button
                    title="Move Device"
                    className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-background flex-shrink-0"
                    onClick={() => handleMoveDevice(device)}
                  >
                    <Move className="w-3.5 h-3.5" />
                  </button>
                  {/* <button
                    title="Duplicate Device"
                    className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-background flex-shrink-0"
                    onClick={() => { setDevicePopup(null); openDuplicateModal(device); }}
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button> */}
                  {/* <button
                    title="Delete Device"
                    className="p-1 rounded-md text-muted-foreground hover:text-red-500 hover:bg-background flex-shrink-0"
                    disabled={deletingDeviceId === device.id}
                    onClick={() => handleDeleteDevice(device)}
                  >
                    {deletingDeviceId === device.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" />
                    )}
                  </button> */}
                </div>
              ))}
            </div>
          </div>
          </>
        )}
        {popup && (
          <div className="absolute bottom-4 left-4 bg-card border border-border rounded-xl shadow-xl p-3 w-64 z-10">
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <p className="text-xs font-bold text-foreground truncate">{popup.name}</p>
                <p className="text-[10px] text-muted-foreground">{popup.level} {popup.code ? `· ${popup.code}` : ''}</p>
              </div>
              <button onClick={() => setPopup(null)} className="text-muted-foreground hover:text-foreground"><X className="w-3.5 h-3.5" /></button>
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              {!popup.isApiNode && (
                <button className="text-[10px] font-semibold px-2 py-1 rounded-md bg-primary/10 text-primary hover:bg-primary/20" onClick={() => requestClose(() => onJumpTo(popup.childRow))}>Open</button>
              )}
              {popup.level === 'DEVICE' ? (
                <button className="text-[10px] font-semibold px-2 py-1 rounded-md bg-secondary text-foreground hover:bg-secondary/70" onClick={() => requestClose(() => onJumpTo(popup.childRow, 'coords'))}>Edit Coordinates</button>
              ) : popup.hasPolygon ? (
                <button className="text-[10px] font-semibold px-2 py-1 rounded-md bg-secondary text-foreground hover:bg-secondary/70" onClick={() => (popup.isApiNode ? startApiNodeEdit(popup.childRow) : startChildEdit(popup.childRow))}>Edit Polygon</button>
              ) : (
                <button className="text-[10px] font-semibold px-2 py-1 rounded-md bg-secondary text-foreground hover:bg-secondary/70" onClick={() => startChildDraw(popup.childRow)}>Draw Polygon</button>
              )}
            </div>
          </div>
        )}
      </div>

      {duplicateModal && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-5 w-96 shadow-2xl">
            <p className="text-sm font-bold text-foreground mb-1">
              {duplicateModal.mode === 'create' ? 'Add Device' : 'Duplicate Device'}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {duplicateModal.mode === 'create' ? (
                'Fill in the details for the new device.'
              ) : (
                <>Duplicating <strong>{duplicateModal.device.name}</strong> — review the fields below. The new device needs its own Device ID.</>
              )}
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device ID *</span>
                <input
                  autoFocus
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={duplicateModal.device_id}
                  onChange={(e) => setDuplicateModal((p) => ({ ...p, device_id: e.target.value }))}
                  placeholder="Enter new device ID"
                />
              </label>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Name</span>
                <input
                  className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  value={duplicateModal.device_name}
                  onChange={(e) => setDuplicateModal((p) => ({ ...p, device_name: e.target.value }))}
                />
              </label>
              <label className="block">
                <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Device Type</span>
                {isAddingDeviceType ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                      placeholder="Type new device type"
                      value={newDeviceTypeInput}
                      onChange={(e) => setNewDeviceTypeInput(e.target.value)}
                      onBlur={() => {
                        if (skipNextDeviceTypeBlurRef.current) { skipNextDeviceTypeBlurRef.current = false; return; }
                        commitNewDeviceType();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          skipNextDeviceTypeBlurRef.current = true;
                          commitNewDeviceType();
                        } else if (e.key === 'Escape') {
                          skipNextDeviceTypeBlurRef.current = true;
                          setIsAddingDeviceType(false);
                          setNewDeviceTypeInput('');
                        }
                      }}
                    />
                  </div>
                ) : (
                  <ReactSelect
                    placeholder="Select a device type"
                    controlHeight={32}
                    options={[
                      { value: '__ADD_NEW_DEVICE_TYPE__', label: <span className="text-[10px] font-semibold">+ Add New Type</span> },
                      ...deviceTypeOptions.map((t) => ({ value: t, label: t })),
                    ]}
                    value={
                      duplicateModal.device_type
                        ? { value: duplicateModal.device_type, label: duplicateModal.device_type }
                        : null
                    }
                    onChange={(opt) => {
                      if (opt?.value === '__ADD_NEW_DEVICE_TYPE__') {
                        setIsAddingDeviceType(true);
                        setNewDeviceTypeInput('');
                        return;
                      }
                      setDuplicateModal((p) => ({ ...p, device_type: opt?.value ?? '' }));
                    }}
                  />
                )}
              </label>
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Latitude</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={duplicateModal.latitude}
                    onChange={(e) => setDuplicateModal((p) => ({ ...p, latitude: e.target.value }))}
                  />
                </label>
                <label className="block">
                  <span className="block text-[10px] font-semibold text-muted-foreground mb-1">Longitude</span>
                  <input
                    className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                    value={duplicateModal.longitude}
                    onChange={(e) => setDuplicateModal((p) => ({ ...p, longitude: e.target.value }))}
                  />
                </label>
              </div>
            </div>
            {duplicateError && <p className="text-[10px] text-red-500 mt-3">{duplicateError}</p>}
            <div className="flex flex-col gap-2 mt-4">
              <button
                className="w-full py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground disabled:opacity-50 flex items-center justify-center gap-1.5"
                onClick={duplicateModal.mode === 'create' ? handleCreateSubmit : handleDuplicateSubmit}
                disabled={duplicatingDeviceId === (duplicateModal.mode === 'create' ? CREATE_DEVICE_ID : duplicateModal.device.id)}
              >
                {duplicatingDeviceId === (duplicateModal.mode === 'create' ? CREATE_DEVICE_ID : duplicateModal.device.id) ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : duplicateModal.mode === 'create' ? (
                  <Plus className="w-3.5 h-3.5" />
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
                {duplicatingDeviceId === (duplicateModal.mode === 'create' ? CREATE_DEVICE_ID : duplicateModal.device.id)
                  ? (duplicateModal.mode === 'create' ? 'Creating...' : 'Duplicating...')
                  : (duplicateModal.mode === 'create' ? 'Create Device' : 'Duplicate Device')}
              </button>
              <button
                className="w-full py-1.5 rounded-lg text-xs font-semibold border border-border text-foreground hover:bg-secondary"
                onClick={() => { setDuplicateModal(null); setDuplicateError(null); setIsAddingDeviceType(false); setNewDeviceTypeInput(''); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-5 w-[420px] shadow-2xl">
            <p className="text-sm font-bold text-destructive mb-1">Delete Device</p>
            <p className="text-xs text-muted-foreground mb-4">
              Are you sure you want to delete this device? This action cannot be undone.
            </p>

            <div className="py-3 px-3 border border-border rounded-lg bg-secondary/40 mb-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[11px] text-muted-foreground">Device Name</span>
                  <span className="text-xs font-medium text-foreground truncate max-w-[220px]">
                    {deleteConfirm.device.name || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[11px] text-muted-foreground">Device ID</span>
                  <span className="text-xs text-foreground truncate max-w-[220px]">
                    {deleteConfirm.device.code || deleteConfirm.device.raw?.device_id || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <span className="text-[11px] text-muted-foreground">Type</span>
                  <span className="text-xs text-foreground truncate max-w-[220px]">
                    {getDeviceTypeName(deleteConfirm.device.deviceType) || '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-4 pt-2 border-t border-border">
                  <span className="text-[11px] text-muted-foreground">Row ID</span>
                  <span className="text-xs text-foreground truncate max-w-[220px]">
                    {deleteConfirm.rowId}
                  </span>
                </div>
              </div>
            </div>

            {deleteError && (
              <p className="text-[10px] text-red-500 mb-3">{deleteError}</p>
            )}

            <div className="flex flex-col gap-2">
              <button
                className="w-full py-2 rounded-lg text-xs font-bold bg-destructive text-white hover:bg-destructive/90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                onClick={confirmDeleteDevice}
                disabled={deletingDeviceId === deleteConfirm.device.id}
              >
                {deletingDeviceId === deleteConfirm.device.id ? (
                  <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Deleting...</>
                ) : (
                  <><Trash2 className="w-3.5 h-3.5" /> Delete Device</>
                )}
              </button>
              <button
                className="w-full py-2 rounded-lg text-xs font-semibold border border-border text-foreground hover:bg-secondary"
                onClick={() => { setDeleteConfirm(null); setDeleteError(null); }}
                disabled={deletingDeviceId === deleteConfirm.device.id}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDiscard && (
        <div className="fixed inset-0 z-[210] bg-black/50 flex items-center justify-center">
          <div className="bg-card border border-border rounded-xl p-5 w-80 shadow-2xl">
            <p className="text-sm font-bold text-foreground mb-1">Unsaved changes</p>
            <p className="text-xs text-muted-foreground mb-4">You have unsaved changes. Discard changes?</p>
            <div className="flex flex-col gap-2">
              <button className="w-full py-1.5 rounded-lg text-xs font-bold bg-primary text-primary-foreground" onClick={() => { handleSaveClick(); confirmDiscard(); setConfirmDiscard(null); }}>Save</button>
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold border border-border text-foreground hover:bg-secondary" onClick={() => { const fn = confirmDiscard; setConfirmDiscard(null); setDirty(false); fn(); }}>Discard</button>
              <button className="w-full py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:bg-secondary" onClick={() => setConfirmDiscard(null)}>Continue Editing</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}